/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { ColorName, Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { CTAType, SectionName, useAvroTracking } from '../../../observability';
import { Link } from './Link';

type ExploreSectionProps = {
  children: ReactNode;
  heading: string;
  image: { altText: string; url: string };

  links: { text: string; url: string }[];
  backgroundColor?: ColorName;
};

const Styles = labelStyles('ExploreSection', {
  root: (backgroundColor: ColorName) =>
    css({
      backgroundColor: backgroundColor ? Colors[backgroundColor] : 'inherit',
      [MEDIA_MIN_WIDTH.large]: {
        padding: '48px 64px',
      },
    }),
  content: css({
    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '1376px',
      display: 'flex',
      margin: '0 auto',
      alignItems: 'center',
    },
  }),
  media: css({
    width: '100%',
    height: '171px',
    objectFit: 'cover',
    objectPosition: '50% 10%',
    '@media (min-width: 500px)': {
      height: '250px',
    },
    [MEDIA_MIN_WIDTH.medium]: {
      height: '351px',
      objectPosition: '50% 5%',
    },
    [MEDIA_MIN_WIDTH.large]: {
      width: '45%',
      height: 'auto',
      marginRight: '131px',
    },
  }),
  textContent: (backgroundColor: ColorName) =>
    css({
      color: Colors.earth,
      backgroundColor: backgroundColor
        ? Colors[backgroundColor]
        : Colors.neutral100,
      padding: '48px 30px',
      [MEDIA_MIN_WIDTH.medium]: {
        padding: '48px 29.5px',
      },
      [MEDIA_MIN_WIDTH.large]: { padding: '0', width: '55%' },
    }),
  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '40px',
    lineHeight: '42px',
    letterSpacing: '1px',
    marginBottom: '50px',
    [MEDIA_MIN_WIDTH.medium]: {
      lineHeight: '100%',
      marginBottom: '86px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '70px',
      lineHeight: '96%',
      letterSpacing: '1.5px',
      maxWidth: '375px',
    },
    [MEDIA_MIN_WIDTH.extraLarge]: {
      marginBottom: '200px',
    },
  }),
  links: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  link: (index: number) =>
    css({
      color: Colors.earth,
      borderTop: index === 0 ? `1px solid ${Colors.earth}` : 'none',
      borderBottom: `1px solid ${Colors.earth}`,
      padding: '25px 0',
      [MEDIA_MIN_WIDTH.medium]: {
        padding: '40px 0',
      },
      [MEDIA_MIN_WIDTH.large]: {
        padding: '34px 0',
      },
    }),
});

export function ExploreSection(props: ExploreSectionProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(props.backgroundColor)}>
      <div css={Styles.content}>
        {props.image?.altText && props.image?.url && (
          <img
            css={Styles.media}
            alt={props.image.altText}
            src={props.image.url}
          />
        )}
        <div css={Styles.textContent(props.backgroundColor)}>
          <h2 css={Styles.heading}>{props.heading}</h2>
          <div css={Styles.links}>
            {(props.links || []).map((link, index) => (
              <div css={Styles.link(index)} key={link.text}>
                <Link
                  href={link.url}
                  onClick={() =>
                    emitCTAClickEvent({
                      href: link.url,
                      section: SectionName.ExploreSection,
                      text: link.text,
                      type: CTAType.cta_link,
                    })
                  }
                >
                  {link.text}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
